import { BaseService } from '@common/http-layer/service/base.service'
import type { FetchError } from '@common/http-layer/service/base.service'
import type {
  CreateOrderRequest,
  CreateTrialSetupIntentRequest,
  CreateTrialSetupIntentResponse,
  OrderResponse,
  PaymentMethod,
  PaymentVerificationRequest,
} from '../types/payment.types'

export class PaymentService extends BaseService {
  constructor() {
    super({
      baseURL: 'PAYMENT_BASE_URL',
      basePath: '',
    })
  }

  /**
   * Create a payment order
   */
  async createOrder(data: CreateOrderRequest): Promise<{ order: OrderResponse }> {
    try {
      const response = await this.post<{ order: OrderResponse }>('/orders', data)
      return response.data
    } catch (error) {
      const errorMessage = ((error as FetchError).response?.data as { message: string }).message || 'Failed to create order'
      throw new Error(errorMessage)
    }
  }

  /**
   * Verify payment after redirect
   */
  async verifyPayment(data: PaymentVerificationRequest): Promise<{ payment: Record<string, unknown> }> {
    try {
      const response = await this.post<{ payment: Record<string, unknown> }>('/stripe/verify', data)

      return response.data
    } catch (error) {
      const errorMessage = ((error as FetchError).response?.data as { message: string }).message || 'Failed to verify payment'
      throw new Error(errorMessage)
    }
  }

  /**
   * Get payment methods for a contact
   */
  async getPaymentMethods(contactId: string): Promise<PaymentMethod[]> {
    try {
      const response = await this.get(`/payment-methods/${contactId}`)
      return response.data as PaymentMethod[]
    } catch (error) {
      const errorMessage = ((error as FetchError).response?.data as { message: string }).message || 'Failed to get payment methods'
      throw new Error(errorMessage)
    }
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(subscriptionId: string, reason?: string): Promise<Record<string, unknown>> {
    try {
      const response = await this.post(`/subscriptions/${subscriptionId}/cancel`, {
        reason,
      })
      return response.data as Record<string, unknown>
    } catch (error) {
      const errorMessage = ((error as FetchError).response?.data as { message: string }).message || 'Failed to cancel subscription'
      throw new Error(errorMessage)
    }
  }

  /**
   * Update subscription
   */
  async updateSubscription(subscriptionId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    try {
      const response = await this.patch(`/subscriptions/${subscriptionId}`, data)
      return response.data as Record<string, unknown>
    } catch (error) {
      const errorMessage = ((error as FetchError).response?.data as { message: string }).message || 'Failed to update subscription'
      throw new Error(errorMessage)
    }
  }

  /**
   * Get subscription details
   */
  async getSubscription(subscriptionId: string): Promise<Record<string, unknown>> {
    try {
      const response = await this.get(`/subscriptions/${subscriptionId}`)
      return response.data as Record<string, unknown>
    } catch (error) {
      const errorMessage = ((error as FetchError).response?.data as { message: string }).message || 'Failed to get subscription'
      throw new Error(errorMessage)
    }
  }

  /**
   * Create a Stripe Setup Intent for trial signup (save card for future charge).
   * Backend creates SetupIntent and returns client_secret for Stripe Payment Element.
   */
  async createTrialSetupIntent(data: CreateTrialSetupIntentRequest): Promise<CreateTrialSetupIntentResponse> {
    try {
      const response = await this.post<CreateTrialSetupIntentResponse>('/trial/setup-intent', data)
      return response.data
    } catch (error) {
      const errorMessage = ((error as FetchError).response?.data as { message: string })?.message || 'Failed to create setup intent'
      throw new Error(errorMessage)
    }
  }

  /**
   * Get default payment provider for a location
   */
  async getDefaultPaymentProvider(
    locationId: string,
    liveMode: boolean
  ): Promise<{
    defaultPaymentProvider: { provider: string } | null
    expressPaymentProvider: { providerConfig?: { merchantClientId?: string } } | null
  }> {
    try {
      const response = await this.get(
        `/integrations/provider/default/public?altId=${locationId}&altType=location&liveMode=${liveMode}&with=express-checkout`
      )
      return response.data as {
        defaultPaymentProvider: { provider: string } | null
        expressPaymentProvider: { providerConfig?: { merchantClientId?: string } } | null
      }
    } catch (error) {
      const errorMessage = ((error as FetchError).response?.data as { message: string })?.message || 'Failed to get payment provider'
      throw new Error(errorMessage)
    }
  }

  /**
   * Initiate payment for communities
   */
  async initiatePayment(contactId: string, orderId: string, sourceId: string): Promise<Record<string, unknown>> {
    try {
      const response = await this.post('/communities/payment/initiate', {
        contactId,
        orderId,
        sourceId,
      })
      return {
        success: true,
        data: response.data,
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initiate payment'
      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  /**
   * Capture payment for communities
   */
  async capturePayment(contactId: string, orderId: string, sourceId: string): Promise<Record<string, unknown>> {
    try {
      const response = await this.post('/communities/payment/capture', {
        contactId,
        orderId,
        sourceId,
      })
      return response.data as Record<string, unknown>
    } catch (error) {
      const errorMessage = ((error as FetchError).response?.data as { message: string }).message || 'Failed to capture payment'
      throw new Error(errorMessage)
    }
  }
}
